import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import type { BookingData } from 'src/modules/booking/containers/booking-wrapper/booking-wrapper.interface'
import { useProviderDetails } from 'src/modules/booking/hooks'
import { LoadingWrapperContext } from 'src/modules/core/context'
import { buildRescheduleBlockedView } from 'src/modules/reschedule/components'
import { useReschedulePageContext } from 'src/modules/reschedule/hooks'
import { RescheduleService } from 'src/services/reschedule.service'
import { getBookingDetailsUrlAfterReschedule } from './reschedule-booking.domain'

export function useRescheduleContext() {
  const { disableLoading } = useContext(LoadingWrapperContext)
  const { urlCode } = useParams<{ urlCode: string }>()
  const [selectedAppointmentTypeId, setSelectedAppointmentTypeId] = useState<string | undefined>(undefined)
  const { data: context, isLoading, isError } = useReschedulePageContext(urlCode ?? '', { enabled: !!urlCode })
  const providerId = context?.providerId
  const { data: providerDetails } = useProviderDetails(providerId ?? '', { enabled: !!providerId })

  useEffect(() => {
    disableLoading()
  }, [disableLoading])

  useEffect(() => {
    if (context?.selected_appointment_type_id != null) {
      setSelectedAppointmentTypeId(context.selected_appointment_type_id)
    }
  }, [context?.selected_appointment_type_id])

  const hasError = !urlCode || isError
  const allowChangingAppointmentType = context?.allow_changing_appointment_type === true
  const appointmentTypes = context?.appointment_types ?? []
  const effectiveSelectedId = selectedAppointmentTypeId ?? context?.selected_appointment_type_id
  const blockedView = useMemo(() => buildRescheduleBlockedView(context), [context])

  const data =
    context == null
      ? null
      : {
          providerId: context.providerId,
          providerName: context.providerName,
          logoUrl: providerDetails?.logo_url,
          initialData: {
            ...context.initialData,
            appointmentType: effectiveSelectedId ?? context.initialData?.appointmentType,
          },
          allowChangingAppointmentType,
          appointmentTypes,
          // Sent with every availability request so the server grandfathers
          // that response; the picker holds no second horizon of its own.
          rescheduleCode: urlCode,
          selectedAppointmentTypeId: effectiveSelectedId,
          setSelectedAppointmentTypeId,
        }

  const handleReschedule = useCallback(
    async (payload: Partial<BookingData>) => {
      const start = (payload as { start_time_utc?: string }).start_time_utc
      const end = (payload as { end_time_utc?: string }).end_time_utc
      if (!urlCode || !start || !end) return
      const appointmentTypeId = allowChangingAppointmentType
        ? (payload.appointmentType ?? effectiveSelectedId)
        : undefined
      await RescheduleService.commit(urlCode, start, end, appointmentTypeId)
      if (context?.source === 'booking' && context.bookingId) {
        window.location.assign(getBookingDetailsUrlAfterReschedule(context.bookingId))
      } else if (context?.source === 'event') {
        window.location.assign(getBookingDetailsUrlAfterReschedule(urlCode))
      }
    },
    [urlCode, context, allowChangingAppointmentType, effectiveSelectedId],
  )

  return {
    isLoading,
    hasError,
    providerId,
    data,
    blockedView,
    handleReschedule,
  }
}
